import React, { useCallback, useMemo } from "react";
import { FiAlertTriangle, FiCloud, FiGlobe, FiLock, FiUsers } from "react-icons/fi";
import WyborOsoby from "./WyborOsoby";
import s from "./SzufladaDysku.module.css";

/* ════════════════════════════════════════════════════════════════════════════════════════
   FORMULARZ WNIOSKU O NOWĄ WITRYNĘ / BIBLIOTEKĘ SHAREPOINT — do okna „Utwórz nowe zgłoszenie”.

   [polecenie właściciela 2026-09-19]
   „dodaj opcję złożenia dysku sieciowego sharepoint”.

   ── DLACZEGO OSOBNY FORMULARZ, A NIE POLE W FORMULARZU NOWEGO DYSKU ──────────────────────
   `FormularzNowegoDysku` składa nazwę techniczną z przedrostka (`KRO_S_FSP_PRO_`), wyprowadza
   ścieżkę UNC (`\\129.1.160.74\projects`) i nazwy DWÓCH grup AD (_RW/_RO). Żadna z tych czterech
   rzeczy nie istnieje dla witryny SharePoint: nie ma udziału sieciowego, nie ma litery dysku,
   a uprawnieniami rządzą grupy witryny w Microsoft 365, nie sAMAccountName w Active Directory.
   Dorzucenie tu gałęzi „a jeśli SharePoint, to pomiń połowę reguł” znaczyłoby, że walidacja nazwy
   zasobu sieciowego działa na czymś, co nigdy nie będzie zasobem sieciowym — a IT dostawałoby
   zgłoszenie z wymyśloną ścieżką `\\129.1.160.74\projects\...` do witryny w chmurze.

   ⚠ TO JEST WNIOSEK, NIE ZAŁOŻENIE. Formularz NIE tworzy niczego w Microsoft 365 — składa treść
   sprawy dla IT. Dlatego nie pyta o nic, czego człowiek spoza IT nie wie (identyfikator witryny,
   szablon, typ grupy) i nie sprawdza zajętości adresu: adres nadaje IT przy realizacji.
   ════════════════════════════════════════════════════════════════════════════════════════ */

/** Kto ma widzieć witrynę. Wartości są też etykietami w treści zgłoszenia — jedno źródło napisu. */
export const ZAKRES_WITRYNY = {
    zespol: "zespol",
    dzial: "dzial",
    firma: "firma",
};

export const ETYKIETY_ZAKRESU = {
    [ZAKRES_WITRYNY.zespol]: "Wskazany zespół (lista osób we wniosku)",
    [ZAKRES_WITRYNY.dzial]: "Cały dział",
    [ZAKRES_WITRYNY.firma]: "Cała firma",
};

const IKONY_ZAKRESU = {
    [ZAKRES_WITRYNY.zespol]: FiUsers,
    [ZAKRES_WITRYNY.dzial]: FiLock,
    [ZAKRES_WITRYNY.firma]: FiGlobe,
};

/** Stan początkowy — eksportowany, żeby okno zgłoszenia nie powtarzało tego kształtu. */
export const PUSTA_NOWA_WITRYNA = {
    nazwa: "",
    zakres: ZAKRES_WITRYNY.zespol,
    uzasadnienie: "",
    wlascicielUserId: null,
    wlascicielNazwa: "",
    wlascicielEmail: "",
};

/**
 * Walidacja — mapa `pole → komunikat`. Pusta mapa = wolno wysłać zgłoszenie.
 * Eksportowana, bo przycisk „Wyślij” w oknie zgłoszenia MUSI pytać o to samo, o co pyta ten ekran.
 */
export const walidujNowaWitryne = (stan = {}) => {
    const bledy = {};

    if (!(stan.nazwa || "").trim()) {
        bledy.nazwa = "Podaj nazwę witryny — bez niej IT nie ma czego założyć.";
    }

    if (!stan.wlascicielUserId) {
        /* Ta sama reguła, co przy dysku sieciowym: zasób bez właściciela-konta to zasób,
           o którego dostęp nie ma kogo zapytać. Przy witrynie jest to dodatkowo osoba,
           która będzie ją administrować w Microsoft 365. */
        bledy.wlasciciel = "Wskaż właściciela witryny — osobę z kontem w aplikacji, która będzie nią zarządzać.";
    }

    if (!(stan.uzasadnienie || "").trim()) {
        bledy.uzasadnienie = "Napisz, do czego ta witryna ma służyć — bez tego IT nie wie, czy ją zakładać.";
    }

    return bledy;
};

/** Treść wniosku doklejana do opisu zgłoszenia. Jedno miejsce składania napisu (jak przy dysku). */
export const opisNowejWitrynyHtml = (stan = {}) => {
    const zakres = ETYKIETY_ZAKRESU[stan.zakres] || ETYKIETY_ZAKRESU[ZAKRES_WITRYNY.zespol];
    return `<h3>Wniosek o założenie witryny SharePoint</h3>
<ul>
  <li><strong>Rodzaj zasobu:</strong> witryna / biblioteka SharePoint (Microsoft 365)</li>
  <li><strong>Nazwa witryny:</strong> ${(stan.nazwa || "").trim() || "—"}</li>
  <li><strong>Zakres dostępu:</strong> ${zakres}</li>
  <li><strong>Właściciel (konto w aplikacji):</strong> ${stan.wlascicielNazwa || "—"}${stan.wlascicielEmail ? ` (${stan.wlascicielEmail})` : ""}</li>
</ul>
<p><strong>Uzasadnienie:</strong> ${(stan.uzasadnienie || "").trim()}</p>`;
};

/**
 * @param {object}   props
 * @param {object}   props.wartosc      stan formularza (patrz {@link PUSTA_NOWA_WITRYNA})
 * @param {Function} props.onZmiana     (nowyStan) => void
 * @param {boolean}  [props.pokazBledy] czy rysować komunikaty (okno włącza je po próbie wysłania)
 */
const FormularzNowejWitrynySharePoint = ({ wartosc = PUSTA_NOWA_WITRYNA, onZmiana, pokazBledy = false }) => {
    /* `stan` w useMemo — inaczej powstaje NOWY obiekt w każdym renderze i useCallback niżej
       przeliczałby się zawsze, czyli nie robiłby tego, po co został napisany. */
    const stan = useMemo(() => ({ ...PUSTA_NOWA_WITRYNA, ...wartosc }), [wartosc]);
    const bledy = useMemo(() => (pokazBledy ? walidujNowaWitryne(stan) : {}), [pokazBledy, stan]);

    const ustaw = useCallback((pole, nowa) => {
        onZmiana?.({ ...stan, [pole]: nowa });
    }, [onZmiana, stan]);

    return (
        <div data-testid="formularz-nowej-witryny">
            <div className={s.sekcja}>
                <h3 className={s.sekcjaTytul}>
                    <FiCloud size={14} aria-hidden="true" /> Nowa witryna SharePoint
                </h3>

                <div className={s.pole}>
                    <label className={s.etykieta} htmlFor="nowa-witryna-nazwa">
                        Nazwa witryny<span className={s.wymagane}>*</span>
                    </label>
                    <input
                        id="nowa-witryna-nazwa"
                        type="text"
                        className={`${s.wejscie} ${pokazBledy && bledy.nazwa ? s.wejscieBledne : ""}`}
                        value={stan.nazwa}
                        onChange={(e) => ustaw("nazwa", e.target.value)}
                        placeholder="np. Projekt Alfa — dokumentacja"
                        aria-invalid={pokazBledy && bledy.nazwa ? "true" : undefined}
                        data-testid="nowa-witryna-nazwa"
                    />
                    {pokazBledy && bledy.nazwa && (
                        <span className={s.komunikatBledu} role="alert">
                            <FiAlertTriangle size={12} /> {bledy.nazwa}
                        </span>
                    )}
                    {/* Adresu witryny NIE pytamy — nadaje go IT przy zakładaniu. Pytanie o adres
                        kończyłoby się wpisanym z palca adresem, który i tak nie powstanie. */}
                    <span className={s.podpowiedz}>
                        Adres witryny nadaje dział IT przy zakładaniu — nie trzeba go podawać.
                    </span>
                </div>

                <div className={s.pole}>
                    <span className={s.etykieta}>Kto ma mieć dostęp</span>
                    <div className={s.przelacznik} role="group" aria-label="Zakres dostępu do witryny">
                        {Object.values(ZAKRES_WITRYNY).map((z) => {
                            const Ikona = IKONY_ZAKRESU[z];
                            return (
                                <button
                                    key={z}
                                    type="button"
                                    className={`${s.przelacznikOpcja} ${stan.zakres === z ? s.przelacznikWybrana : ""}`}
                                    onClick={() => ustaw("zakres", z)}
                                    aria-pressed={stan.zakres === z}
                                    data-testid={`nowa-witryna-zakres-${z}`}
                                >
                                    <Ikona size={14} /> {ETYKIETY_ZAKRESU[z]}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className={s.pole}>
                    <label className={s.etykieta} htmlFor="nowa-witryna-wlasciciel">
                        Właściciel witryny<span className={s.wymagane}>*</span>
                    </label>
                    <WyborOsoby
                        idPola="nowa-witryna-wlasciciel"
                        aktywne
                        wybrana={stan.wlascicielUserId
                            ? { id: stan.wlascicielUserId, firstName: stan.wlascicielNazwa, lastName: "", email: stan.wlascicielEmail }
                            : null}
                        onWybierz={(osoba) => onZmiana?.({
                            ...stan,
                            wlascicielUserId: osoba?.id ?? null,
                            wlascicielNazwa: osoba ? `${osoba.firstName || ""} ${osoba.lastName || ""}`.trim() : "",
                            wlascicielEmail: osoba?.email || "",
                        })}
                        bledny={Boolean(pokazBledy && bledy.wlasciciel)}
                        placeholder="Wyszukaj pracownika w aplikacji..."
                    />
                    {pokazBledy && bledy.wlasciciel && (
                        <span className={s.komunikatBledu} role="alert">
                            <FiAlertTriangle size={12} /> {bledy.wlasciciel}
                        </span>
                    )}
                </div>

                <div className={s.pole}>
                    <label className={s.etykieta} htmlFor="nowa-witryna-uzasadnienie">
                        Do czego ma służyć<span className={s.wymagane}>*</span>
                    </label>
                    <textarea
                        id="nowa-witryna-uzasadnienie"
                        className={`${s.obszarTekstu} ${pokazBledy && bledy.uzasadnienie ? s.wejscieBledne : ""}`}
                        value={stan.uzasadnienie}
                        onChange={(e) => ustaw("uzasadnienie", e.target.value)}
                        rows={3}
                        placeholder="np. wspólne miejsce na dokumentację projektu Alfa dla zespołu projektowego i podwykonawcy."
                        data-testid="nowa-witryna-uzasadnienie"
                    />
                    {pokazBledy && bledy.uzasadnienie && (
                        <span className={s.komunikatBledu} role="alert">
                            <FiAlertTriangle size={12} /> {bledy.uzasadnienie}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FormularzNowejWitrynySharePoint;
